import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { getAccessToken } from '../../../../app/Slices/AuthSlice';
import { useRefreshMutation } from '../../../../app/Slices/SevenDayApiSlice';
import { VehiclePosition } from '../../../types/vehicles';

export function useVehicleTracking() {
    const dispatch = useDispatch<any>();
    const token = useSelector(getAccessToken) as string | null;
    const socketRef = useRef<Socket | null>(null);
    const tokenRef = useRef<string | null>(null);
    const refreshInProgressRef = useRef(false);
    const [vehicles, setVehicles] = useState<Map<number, VehiclePosition>>(
        new Map(),
    );
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refresh] = useRefreshMutation();

    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    useEffect(() => {
        if (!token) {
            setConnected(false);
            return;
        }

        const socket = io(`${process.env.REACT_APP_BASE_URL}/vehicles`, {
            transports: ['websocket'],
            auth: { token },
        });

        socket.on('connect', () => {
            setConnected(true);
            setError(null);
            refreshInProgressRef.current = false;
        });

        socket.on('disconnect', () => {
            setConnected(false);
        });

        socket.on('connect_error', async (err) => {
            setError(err.message);
            setConnected(false);

            if (
                err.message === 'Unauthorized' &&
                !refreshInProgressRef.current
            ) {
                refreshInProgressRef.current = true;
                try {
                    const refreshedAuth = await refresh({}).unwrap();

                    const refreshedToken = refreshedAuth?.accessToken;
                    if (refreshedToken) {
                        tokenRef.current = refreshedToken;
                        socket.auth = { token: refreshedToken };
                        socket.connect();
                        return;
                    }
                } catch {
                    //
                } finally {
                    refreshInProgressRef.current = false;
                }
            }
        });

        socket.on('message', (data: VehiclePosition) => {
            setVehicles((prev) => {
                const next = new Map(prev);
                next.set(data.module.id, data);
                return next;
            });
        });

        socketRef.current = socket;

        return () => {
            socket.disconnect();
            socketRef.current = null;
            refreshInProgressRef.current = false;
        };
    }, [dispatch, token]);

    const vehicleList = Array.from(vehicles.values());

    return { vehicleList, connected, error };
}
